/**
 * 后台 - 视频系统配置
 *
 * 调整 worker 行为、并发上限、超时、重试、孤儿清理参数。
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  videoSystemConfigApi,
  type VideoSystemConfigView,
} from '../services/videoAdminApi';
import toast from '../../../components/Toast';
import { formatBeijingDateTime } from '../../../utils/time';

export default function VideoSystemConfigTab() {
  const [config, setConfig] = useState<VideoSystemConfigView | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 表单字段
  const [maxConcurrentPerUser, setMaxConcurrentPerUser] = useState(8);
  const [maxJobsPerSubmit, setMaxJobsPerSubmit] = useState(4);
  const [taskTimeoutSeconds, setTaskTimeoutSeconds] = useState(900);
  const [workerIntervalMs, setWorkerIntervalMs] = useState(5000);
  const [pollUpstreamIntervalMs, setPollUpstreamIntervalMs] = useState(8000);
  const [downloadRetryCount, setDownloadRetryCount] = useState(2);
  const [downloadRetryDelayMs, setDownloadRetryDelayMs] = useState(5000);
  const [orphanCleanupEnabled, setOrphanCleanupEnabled] = useState(true);
  const [orphanCleanIntervalMs, setOrphanCleanIntervalMs] = useState(3_600_000);
  const [orphanRetentionMs, setOrphanRetentionMs] = useState(86_400_000);

  const load = async () => {
    setLoading(true);
    try {
      const data = await videoSystemConfigApi.get();
      setConfig(data);
      setMaxConcurrentPerUser(data.maxConcurrentPerUser);
      setMaxJobsPerSubmit(data.maxJobsPerSubmit);
      setTaskTimeoutSeconds(data.taskTimeoutSeconds);
      setWorkerIntervalMs(data.workerIntervalMs);
      setPollUpstreamIntervalMs(data.pollUpstreamIntervalMs);
      setDownloadRetryCount(data.downloadRetryCount);
      setDownloadRetryDelayMs(data.downloadRetryDelayMs);
      setOrphanCleanupEnabled(data.orphanCleanupEnabled);
      setOrphanCleanIntervalMs(data.orphanCleanIntervalMs);
      setOrphanRetentionMs(data.orphanRetentionMs);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await videoSystemConfigApi.update({
        maxConcurrentPerUser,
        maxJobsPerSubmit,
        taskTimeoutSeconds,
        workerIntervalMs,
        pollUpstreamIntervalMs,
        downloadRetryCount,
        downloadRetryDelayMs,
        orphanCleanupEnabled,
        orphanCleanIntervalMs,
        orphanRetentionMs,
      });
      setConfig(updated);
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-light-muted">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        加载中
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-light-text">视频系统配置</h2>
        <p className="text-sm text-light-muted mt-1">
          调整视频任务调度器行为；改完会立即生效（最长延迟 30 秒缓存）。
        </p>
      </div>

      <SectionCard title="任务并发">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="单用户并发上限"
            hint="pending + processing 同时存在的最多数量"
            value={maxConcurrentPerUser}
            onChange={setMaxConcurrentPerUser}
            min={1}
            max={100}
          />
          <NumberField
            label="单次提交最多任务数"
            hint="一次表单点击生成的最大数量"
            value={maxJobsPerSubmit}
            onChange={setMaxJobsPerSubmit}
            min={1}
            max={20}
          />
        </div>
      </SectionCard>

      <SectionCard title="超时控制">
        <NumberField
          label="单任务硬超时（秒）"
          hint="进入 pending / submitting / processing 后的最大等待时间，超过自动 timeout 退款"
          value={taskTimeoutSeconds}
          onChange={setTaskTimeoutSeconds}
          min={60}
          max={3 * 60 * 60}
        />
      </SectionCard>

      <SectionCard title="Worker 节奏">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="扫描间隔（毫秒）"
            hint="worker 多久跑一轮（建议 5000）"
            value={workerIntervalMs}
            onChange={setWorkerIntervalMs}
            min={1000}
            max={5 * 60 * 1000}
          />
          <NumberField
            label="单任务调上游 poll 间隔（毫秒）"
            hint="同一任务两次调上游查询的最小间隔"
            value={pollUpstreamIntervalMs}
            onChange={setPollUpstreamIntervalMs}
            min={1000}
            max={5 * 60 * 1000}
          />
        </div>
      </SectionCard>

      <SectionCard title="下载重试">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="重试次数"
            hint="视频从上游下载失败时的最大重试次数"
            value={downloadRetryCount}
            onChange={setDownloadRetryCount}
            min={0}
            max={10}
          />
          <NumberField
            label="重试间隔（毫秒）"
            hint="两次重试之间等待的时间"
            value={downloadRetryDelayMs}
            onChange={setDownloadRetryDelayMs}
            min={100}
            max={5 * 60 * 1000}
          />
        </div>
      </SectionCard>

      <SectionCard title="孤儿文件清理">
        <label className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-light-border bg-light-hover/30 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-light-text">启用自动清理</p>
            <p className="mt-1 text-xs text-light-muted">
              关闭后 worker 不再扫描并删除无数据库记录的视频孤儿目录。
            </p>
          </div>
          <input
            type="checkbox"
            checked={orphanCleanupEnabled}
            onChange={(e) => setOrphanCleanupEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-light-border"
          />
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="清理扫描间隔（毫秒）"
            hint="多久扫描一次 storage（建议 3600000 = 1 小时）"
            value={orphanCleanIntervalMs}
            onChange={setOrphanCleanIntervalMs}
            min={60 * 1000}
            max={24 * 60 * 60 * 1000}
            disabled={!orphanCleanupEnabled}
          />
          <NumberField
            label="孤儿保留时长（毫秒）"
            hint="数据库无对应记录的目录超过该时长才删（建议 86400000 = 24 小时）"
            value={orphanRetentionMs}
            onChange={setOrphanRetentionMs}
            min={60 * 60 * 1000}
            max={30 * 24 * 60 * 60 * 1000}
            disabled={!orphanCleanupEnabled}
          />
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存
        </button>
      </div>

      {config?.updatedAt && (
        <p className="text-xs text-light-muted text-right">
          最后修改：{formatBeijingDateTime(config.updatedAt)}
        </p>
      )}
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-light-border rounded-2xl p-5">
      <h3 className="font-semibold text-light-text mb-3">{title}</h3>
      {children}
    </div>
  );
}

function NumberField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  disabled,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-light-text block mb-1.5">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        disabled={disabled}
        className="input-field w-full disabled:cursor-not-allowed disabled:opacity-60"
      />
      {hint && <p className="text-xs text-light-muted mt-1">{hint}</p>}
    </div>
  );
}
